const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const buildBackendUrl = (path) => {
  const backendUri = process.env.REACT_APP_BACKEND_URI || process.env.BACKEND_URI || "";
  return backendUri ? `${backendUri}${path}` : path;
};

const fetchJson = async (path) => {
  const res = await fetch(buildBackendUrl(path));
  if (!res.ok) {
    throw new Error(`${path} failed (${res.status})`);
  }

  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error(`${path} returned non-JSON response`);
  }

  return res.json();
};

const buildPredictionSummary = (predictionRows = []) => {
  const rows = Array.isArray(predictionRows) ? predictionRows : [];
  const total = rows.reduce((sum, row) => {
    const value = Number(row?.["Predicted_Total_Chlorine_Demand_kg/h"] ?? 0);
    return sum + (Number.isFinite(value) ? value * 24 : 0);
  }, 0);

  const peak = rows.reduce((max, row) => {
    const value = Number(row?.["Predicted_Total_Chlorine_Demand_kg/h"] ?? 0) * 24;
    const safe = Number.isFinite(value) ? value : 0;
    return safe > max ? safe : max;
  }, 0);

  const avg = rows.length > 0 ? total / rows.length : 0;

  return {
    rows: rows.length,
    totalDailyForecastKg: Number(total.toFixed(2)),
    averageDailyForecastKg: Number(avg.toFixed(2)),
    peakDailyForecastKg: Number(peak.toFixed(2)),
  };
};

const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const renderDashboardKpis = (kpiData = {}) => {
  const rawTurbidity = kpiData?.rawTurbidity || {};
  const rawWaterPh = kpiData?.rawWaterPh || {};
  const conductivity = kpiData?.conductivity || {};
  const chlorine = kpiData?.chlorineDosage || {};

  return `
  <div class="kpi-grid">
    <div class="kpi-card"><h3>Raw Turbidity</h3><p class="value">${escapeHtml(rawTurbidity.value || "-")} ${escapeHtml(rawTurbidity.unit || "")}</p><p class="sub">${escapeHtml(rawTurbidity.subText || "")}</p></div>
    <div class="kpi-card"><h3>Raw Water pH</h3><p class="value">${escapeHtml(rawWaterPh.value || "-")} ${escapeHtml(rawWaterPh.unit || "")}</p><p class="sub">${escapeHtml(rawWaterPh.subText || "")}</p></div>
    <div class="kpi-card"><h3>Conductivity</h3><p class="value">${escapeHtml(conductivity.value || "-")} ${escapeHtml(conductivity.unit || "")}</p><p class="sub">${escapeHtml(conductivity.subText || "")}</p></div>
    <div class="kpi-card"><h3>Chlorine Dosage</h3><p class="value">${escapeHtml(chlorine.value || "-")} ${escapeHtml(chlorine.unit || "")}</p><p class="sub">Recommended: ${escapeHtml(chlorine.recommended || "-")} ${escapeHtml(chlorine.unit || "")}</p></div>
  </div>`;
};

const renderComparative = (comparativeData = {}) => {
  const daily = comparativeData?.todayVsYesterday || {};
  const hourly = comparativeData?.hourVsPrevious || {};

  const renderRows = (rows = [], leftLabel, rightLabel, leftKey, rightKey) =>
    (rows || [])
      .map(
        (item) => `<tr>
      <td>${escapeHtml(item?.name || "-")}</td>
      <td>${escapeHtml(item?.[leftKey] || "-")}</td>
      <td>${escapeHtml(item?.[rightKey] || "-")}</td>
    </tr>`
      )
      .join("");

  return `
  <div class="two-col">
    <div class="panel">
      <h3>Comparative Analysis (Daily)</h3>
      <table>
        <thead><tr><th>Metric</th><th>${escapeHtml(daily.todayLabel || "Today")}</th><th>${escapeHtml(daily.yesterdayLabel || "Yesterday")}</th></tr></thead>
        <tbody>${renderRows(daily.metrics, daily.todayLabel, daily.yesterdayLabel, "today", "yesterday")}</tbody>
      </table>
    </div>
    <div class="panel">
      <h3>Comparative Analysis (Hourly)</h3>
      <table>
        <thead><tr><th>Metric</th><th>${escapeHtml(hourly.currentHourLabel || "Current")}</th><th>${escapeHtml(hourly.previousHourLabel || "Previous")}</th></tr></thead>
        <tbody>${renderRows(hourly.metrics, hourly.currentHourLabel, hourly.previousHourLabel, "current", "previous")}</tbody>
      </table>
    </div>
  </div>`;
};

const renderAlerts = (alertsData = []) => {
  if (!Array.isArray(alertsData) || alertsData.length === 0) {
    return `<p class="sub">No alerts available.</p>`;
  }

  return `<ul class="alerts">${alertsData
    .map(
      (alert) => `<li class="alert alert-${escapeHtml(alert?.type || "yellow")}" >
      <strong>${escapeHtml(alert?.title || "Alert")}</strong>
      <span>${escapeHtml(alert?.desc || "")}</span>
    </li>`
    )
    .join("")}</ul>`;
};

const renderPredictionForecastTable = (predictionRows = []) => {
  if (!Array.isArray(predictionRows) || predictionRows.length === 0) {
    return `<p class="sub">No prediction forecast rows found.</p>`;
  }

  const rows = predictionRows.slice(0, 31);
  const tableRows = rows
    .map((row) => {
      const date = row?.Date || row?.date || "-";
      const hourly = toNum(row?.["Predicted_Total_Chlorine_Demand_kg/h"]);
      const daily = hourly * 24;
      return `<tr>
        <td>${escapeHtml(date)}</td>
        <td>${escapeHtml(hourly.toFixed(2))}</td>
        <td>${escapeHtml(daily.toFixed(2))}</td>
      </tr>`;
    })
    .join("");

  const extraText = predictionRows.length > rows.length
    ? `<p class="sub">Showing first ${rows.length} rows out of ${predictionRows.length}.</p>`
    : "";

  return `${extraText}
  <table>
    <thead><tr><th>Date</th><th>Predicted kg/h</th><th>Daily Demand (kg)</th></tr></thead>
    <tbody>${tableRows}</tbody>
  </table>`;
};

const renderMonthlyConsumption = (monthlyConsumption = []) => {
  if (!Array.isArray(monthlyConsumption) || monthlyConsumption.length === 0) {
    return `<p class="sub">No monthly consumption data found.</p>`;
  }

  const max = monthlyConsumption.reduce((m, item) => {
    const v = toNum(item?.monthly_chlorine_consumption_l);
    return v > m ? v : m;
  }, 0);

  const bars = monthlyConsumption
    .map((item) => {
      const value = toNum(item?.monthly_chlorine_consumption_l);
      const widthPct = max > 0 ? (value / max) * 100 : 0;
      return `<div class="bar-row">
        <div class="bar-label">${escapeHtml(item?.month_label || "-")}</div>
        <div class="bar-track"><div class="bar-fill" style="width:${widthPct.toFixed(2)}%"></div></div>
        <div class="bar-value">${escapeHtml(value.toFixed(2))} L</div>
      </div>`;
    })
    .join("");

  return `<div class="bars">${bars}</div>`;
};

export const generateUnifiedDashboardPredictionReport = async ({
  dashboardSnapshot,
  selectedYear,
}) => {
  const generatedAt = new Date();
  const predictionPayload = await fetchJson("/api/predict");
  const monthlyPayload = await fetchJson(
    `/api/predictions/chlorine-consumption/monthly?year=${selectedYear}`
  );

  const predictionRows = Array.isArray(predictionPayload?.predictions)
    ? predictionPayload.predictions
    : [];

  const monthlyConsumption = Array.isArray(monthlyPayload?.monthly_consumption)
    ? monthlyPayload.monthly_consumption
    : [];

  const predictionSummary = buildPredictionSummary(predictionRows);

  const report = {
    metadata: {
      generatedAt: generatedAt.toISOString(),
      reportType: "Dashboard + Prediction",
      selectedYear,
    },
    dashboard: dashboardSnapshot,
    prediction: {
      summary: predictionSummary,
      forecastRows: predictionRows,
      monthlyConsumption,
    },
  };

  const chartImages = dashboardSnapshot?.chartImages || {};
  const chartCards = [
    { key: "chlorine", title: "Chlorine Dosage: Actual vs Forecast" },
    { key: "rawWater", title: "Raw Water Quality Trends" },
    { key: "chemical", title: "Other Chemical Dosage" },
    { key: "intake", title: "Intake Flow" },
  ];

  const chartsHtml = chartCards
    .map(({ key, title }) => {
      const src = chartImages[key];
      if (!src) {
        return `<div class="chart-card"><h3>${escapeHtml(title)}</h3><p class="sub">Chart image unavailable.</p></div>`;
      }

      return `<div class="chart-card"><h3>${escapeHtml(title)}</h3><img src="${src}" alt="${escapeHtml(title)}" /></div>`;
    })
    .join("");

  const dashboardSnapshotCards = renderDashboardKpis(dashboardSnapshot?.kpiData);

  const comparativeHtml = renderComparative(dashboardSnapshot?.comparativeData);
  const alertsHtml = renderAlerts(dashboardSnapshot?.alertsData);
  const predictionRowsHtml = renderPredictionForecastTable(predictionRows);
  const monthlyConsumptionHtml = renderMonthlyConsumption(monthlyConsumption);

  const html = `<!doctype html>
<html>
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>ChloroCast Unified Report</title>
  <style>
    body { font-family: "Segoe UI", Arial, sans-serif; margin: 0; color: #0f172a; background: #f8fafc; }
    .page { max-width: 1320px; margin: 0 auto; padding: 26px 20px 40px; }
    h1, h2, h3 { margin: 0 0 12px 0; }
    h1 { color: #015486; font-size: 34px; }
    h2 { margin-top: 30px; color: #1d4e89; font-size: 24px; border-bottom: 2px solid #dbe5f0; padding-bottom: 8px; }
    h3 { color: #1e3a5f; font-size: 18px; }
    p { margin: 4px 0; }
    .sub { color: #4b5563; font-size: 14px; }
    .meta { padding: 14px; background: #eef6ff; border: 1px solid #cfe2f7; border-radius: 10px; }
    .chip-row { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
    .chip { background: #e8f2fa; color: #16476f; border-radius: 999px; padding: 6px 10px; font-size: 13px; }
    .prediction-hero { margin-top: 16px; border: 1px solid #bfd8f0; border-radius: 12px; background: linear-gradient(145deg, #eef7ff, #dff1ff); padding: 14px; }
    .prediction-hero h2 { margin-top: 0; border-bottom: none; padding-bottom: 0; }
    .prediction-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; margin-top: 10px; }
    .prediction-kpi { background: #ffffff; border: 1px solid #d3e5f5; border-radius: 10px; padding: 12px; }
    .prediction-kpi-label { color: #1e3a5f; font-size: 13px; }
    .prediction-kpi-value { color: #015486; font-size: 30px; font-weight: 800; line-height: 1.1; margin-top: 6px; }
    .kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 12px; margin-top: 12px; }
    .kpi-card { background: #ffffff; border: 1px solid #dbe5f0; border-radius: 10px; padding: 12px; }
    .kpi-card .value { font-size: 24px; font-weight: 700; color: #015486; margin: 6px 0; }
    .charts-grid { display: grid; grid-template-columns: 1fr; gap: 16px; margin-top: 12px; }
    .chart-card { background: #ffffff; border: 1px solid #dbe5f0; border-radius: 12px; padding: 14px; }
    .chart-card img { width: 100%; height: auto; min-height: 420px; object-fit: contain; border-radius: 8px; border: 1px solid #dbe5f0; background: #fff; }
    .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
    .panel { background: #ffffff; border: 1px solid #dbe5f0; border-radius: 10px; padding: 12px; }
    table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 8px; overflow: hidden; }
    th, td { border-bottom: 1px solid #e5edf5; padding: 8px; text-align: left; font-size: 13px; }
    th { background: #f1f7fd; color: #1e3a5f; }
    .prediction-table-wrap { border: 1px solid #cfe2f7; border-radius: 12px; background: #ffffff; padding: 12px; }
    .prediction-table-wrap table th { background: #eaf4ff; }
    .prediction-table-wrap table td:nth-child(2), .prediction-table-wrap table td:nth-child(3) { font-weight: 700; color: #0c4a6e; }
    .alerts { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
    .alert { padding: 10px; border-radius: 8px; border: 1px solid; display: flex; flex-direction: column; gap: 3px; }
    .alert-red { background: #fff1f2; border-color: #fecdd3; color: #9f1239; }
    .alert-yellow { background: #fffbeb; border-color: #fde68a; color: #92400e; }
    .bars { display: grid; gap: 8px; }
    .bar-row { display: grid; grid-template-columns: 120px 1fr 140px; gap: 10px; align-items: center; }
    .bar-label, .bar-value { font-size: 13px; color: #334155; }
    .bar-track { height: 14px; background: #e2e8f0; border-radius: 999px; overflow: hidden; }
    .bar-fill { height: 100%; background: linear-gradient(90deg, #015486, #3b82f6); }
    @media (max-width: 900px) {
      .two-col { grid-template-columns: 1fr; }
      .bar-row { grid-template-columns: 1fr; gap: 6px; }
      .chart-card img { min-height: 280px; }
    }
  </style>
</head>
<body>
  <main class="page">
    <h1>ChloroCast Full Operations Report</h1>
    <div class="meta">
      <p><strong>Generated:</strong> ${escapeHtml(generatedAt.toLocaleString())}</p>
      <p><strong>Report Scope:</strong> Dashboard + Prediction page data</p>
      <p><strong>Monthly Year:</strong> ${escapeHtml(selectedYear)}</p>
      <div class="chip-row"><span class="chip">Prediction-centered report</span><span class="chip">Dashboard support insights</span></div>
    </div>

    <section class="prediction-hero">
      <h2>Prediction Results Overview</h2>
      <div class="prediction-kpis">
        <div class="prediction-kpi"><div class="prediction-kpi-label">Forecast Rows</div><div class="prediction-kpi-value">${escapeHtml(predictionSummary.rows)}</div></div>
        <div class="prediction-kpi"><div class="prediction-kpi-label">Total Forecast (kg)</div><div class="prediction-kpi-value">${escapeHtml(predictionSummary.totalDailyForecastKg)}</div></div>
        <div class="prediction-kpi"><div class="prediction-kpi-label">Average Daily Forecast (kg)</div><div class="prediction-kpi-value">${escapeHtml(predictionSummary.averageDailyForecastKg)}</div></div>
        <div class="prediction-kpi"><div class="prediction-kpi-label">Peak Daily Forecast (kg)</div><div class="prediction-kpi-value">${escapeHtml(predictionSummary.peakDailyForecastKg)}</div></div>
      </div>
    </section>

    <h2>Prediction Forecast Details</h2>
    <div class="prediction-table-wrap">${predictionRowsHtml}</div>

    <h2>Prediction Monthly Chlorine Consumption (${escapeHtml(selectedYear)})</h2>
    ${monthlyConsumptionHtml}

    <h2>Dashboard KPI Snapshot</h2>
    ${dashboardSnapshotCards}

    <h2>Dashboard Graphs</h2>
    <div class="charts-grid">${chartsHtml}</div>

    <h2>Dashboard Comparative Analysis</h2>
    ${comparativeHtml}

    <h2>Dashboard Alerts Log</h2>
    ${alertsHtml}
  </main>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = `chloro-report-${generatedAt.toISOString().replace(/[:.]/g, "-")}.html`;
  anchor.click();
  URL.revokeObjectURL(objectUrl);

  return report;
};
